import { Injectable, Inject } from "@angular/core";
import { ENV_VARS } from "../environment-variables/environment-variables.module";
import {
  SubTaskObject,
  DIAPROFIL_ITEM_EVAL_QUESTIONNAIRE_UUID,
  ITypewriterResult
} from "../models/types";
import {
  QuestionnairePage,
  QuestionnaireState,
  QuestionnaireService
} from "@zitelab/ionic-fhir-questionnaires/dist";
import { LoadingController, App, NavParams, Events } from "ionic-angular";
import { TranslateService } from "@ngx-translate/core";
import { first } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { AppState } from "../models/app.state";
import { UserState } from "../store/reducers/user.reducer";
import { getFlattenedFhir } from "../store/selectors/fhir.selectors";
import { FhirService } from "./fhir.service";
import { codingMatch } from "../utils/coding-match";
import { DiaprofilPatient } from "../models/diaprofil-patient";
import { HomePage } from "../../pages/home.page/home.page";
import { ConfigState } from "../store/reducers/config.reducer";

@Injectable()
export class DiaProfilService {
  userState: UserState;
  subTaskIds: string[];
  curParentTask: fhir.Task;
  sourceNav: { pageComponent: any; navParams: NavParams };
  configState: ConfigState;
  constructor(
    @Inject(ENV_VARS) public envVars,
    private app: App,
    public translate: TranslateService,
    public loadingCtrl: LoadingController,
    public store: Store<AppState>,
    public fhirService: FhirService,
    public questionnaireService: QuestionnaireService,
    public events: Events
  ) {
    this.store
      .select(state => state.user)
      .subscribe(user => {
        this.userState = user;
      });
    this.store
      .select(state => state.config)
      .subscribe(config => {
        this.configState = config;
      });
  }

  createQuestionnairePageParams(params: {
    subTask: SubTaskObject;
    sourceRef: fhir.Reference;
    subjectRef: fhir.Reference;
    authorRef: fhir.Reference;
    optParams: {};
  }) {
    const getDisplayType = (
      questionnaireResource: fhir.Questionnaire
    ): "widget" | "page" | "full" => {
      const customSettings: { [key: string]: "widget" | "page" | "full" } = {
        "ba7c1eb8-94d2-48dd-8741-1b72e8ea03cf": "widget", // VBS_PRO_DIA_CONSENT
        "625bb0c5-1a74-4c7f-a2eb-33cca0db8289": "widget", // M_PRODIA_CONSENT
        "89a5cac9-1937-424a-a12d-9390b47ad68f": "widget", // VBS_PRO_DIA
        "c251166c-9359-4912-b6e9-6ab428a7d5e9": "page", // DIAPROFIL_PATIENT_POST_PRO_EVAL
        "e788a6f2-3d1f-4cbd-9339-63e7c0219a79": "page", // VBS_PRO_DIA_PATIENT_POST_VISIT_EVAL
        "0b876ba5-935d-4cfe-a5fd-f3d05e517a4c": "page", // M_PRODIA_HCP_POST_VISIT_EVAL
        "6d141ae4-a4fa-4828-984c-34e773574fdc": "page", // VBS_PRO_DIA_HCP_POST_VISIT_EVAL
        "5afd0eff-2d20-4412-be27-c3de3a4695fe": "widget", // DIAPROFIL_HCP_DASHBOARD_ITEM_NOTES
        "44bdcc0f-bf5c-45f2-9586-d5dabf7d27e6": "widget", // M_PRODIA_PATIENT_POST_PRO_DEMO
        "619c2bde-94da-4a44-8401-983bb3c84295": "full", // M_PRODIA_HCP_POST_VISIT_DEMO
        "25de7e35-5be8-4290-acea-695b3f34a193": "widget", // DIAPROFIL_HCP_BASELINE_PROJECT
        "5d301529-da4b-4647-b274-d6084ae1cf1a": "page", // DIAPROFIL_HCP_END_PROJECT
        "9c7a3293-a8cd-427b-983b-6249727df22d": "widget", // COPENHAGEN_PATIENT_POST_PRO_DEMO
        "cee1bdc3-c209-45ab-b448-97e0771ad837": "page" // KRAM_HEALTH_FACTORS
      };
      return customSettings[questionnaireResource.id] || "widget";
    };
    return {
      taskId: params.subTask.task.id,
      taskStatus: params.subTask.task.status,
      sourceRef: params.sourceRef,
      subjectRef: params.subjectRef,
      authorRef: params.authorRef,
      procedureRequestId: params.subTask.procedureRequest.id,
      questionnaireResource: params.subTask.questionnaire,
      displayOptions: {
        type: getDisplayType(params.subTask.questionnaire),
        value: "next"
      },
      displayProgress: {
        enabled: false,
        type: null
      },
      title: "DiaProfil behandler evaluering",
      navbarColor: "primary",
      menuConfig: {
        enabled: true
      },
      alertWhenEmptySubmit: false,
      submitButtonName: "IFQ.SUBMIT",
      ...params.optParams
    };
  }

  onSubmitOfQuestionnaire = (
    event,
    info: {
      taskId: string;
      sourceRef: fhir.Reference;
      subjectRef: fhir.Reference;
      authorRef: fhir.Reference;
      procedureRequestId: string;
      resource: fhir.Questionnaire;
      onSubmit: Function;
    }
  ) => {
    const subTaskIndex = this.subTaskIds.findIndex(
      subTaskId => subTaskId === info.taskId
    );
    let questionnaireState: QuestionnaireState;
    this.store
      .select(state => state.questionnaires)
      .subscribe(res => {
        questionnaireState = res.find(elem => elem.taskId === info.taskId);
      });
    const nextIndex = subTaskIndex + 1;
    const nextSubTask: SubTaskObject = this.fhirService.subTasks[
      this.subTaskIds[nextIndex]
    ];
    const getParams = subTask => {
      return this.createQuestionnairePageParams({
        subTask: subTask,
        sourceRef: info.sourceRef,
        subjectRef: info.subjectRef,
        authorRef: info.authorRef,
        optParams: {
          submitButtonName: "IFQ.SUBMIT",
          onSubmit: info.onSubmit
        }
      });
    };
    const loading = this.loadingCtrl.create({
      content: this.translate.instant(
        "GENERIC_UI.SUBMITTING_QUESTIONNAIRE_MSG"
      ),
      dismissOnPageChange: true
    });
    loading.present();
    const questionnaireResponse = questionnaireState.questionnaireResponse;
    questionnaireResponse.status = "completed";
    this.fhirService
      .postQuestionnaireResponse({
        responseResource: questionnaireResponse,
        procedureRequestId: questionnaireState.procedureRequestId,
        subTaskId: questionnaireState.taskId
      })
      .pipe()
      .subscribe(
        elem => console.log(elem),
        error => console.log(error),
        () => {
          if (nextIndex < this.subTaskIds.length) {
            const rootNav = this.app.getRootNav();
            rootNav.setRoot(QuestionnairePage, getParams(nextSubTask));
          } else {
            this.fhirService
              .patchResource({
                resourceType: "Task",
                resourceId: this.curParentTask.id,
                operations: [
                  {
                    op: "replace",
                    path: "/status",
                    value: "completed"
                  }
                ]
              })
              .subscribe(result => {
                const rootNav = this.app.getRootNav();
                if (this.sourceNav !== null) {
                  rootNav.push(
                    this.sourceNav.pageComponent,
                    this.sourceNav.navParams,
                    () => {
                      console.log("Used sourceNav!");
                    }
                  );
                  this.sourceNav = null; // resetting source nav
                } else {
                  rootNav.setRoot(HomePage);
                }
              });
          }
        }
      );
  };

  goToQuestionnaires(params: {
    parentTask: fhir.Task;
    subTaskCondition?: (inputElem: fhir.TaskInput) => {};
    overriddenParams?: {};
    sourceRef: fhir.Reference;
    subjectRef: fhir.Reference;
    authorRef: fhir.Reference;
    sourceNav?: { pageComponent: any; navParams: NavParams };
  }) {
    if (params.subTaskCondition === undefined) {
      params.subTaskCondition = elem => true;
    }
    if (params.overriddenParams === undefined) {
      params.overriddenParams = {};
    }

    if (params.sourceNav === undefined) {
      this.sourceNav = null;
    } else {
      this.sourceNav = params.sourceNav;
    }

    this.curParentTask = params.parentTask;
    const loading = this.loadingCtrl.create({
      content: this.translate.instant("GENERIC_UI.LOADING_MSG"),
      dismissOnPageChange: true
    });
    loading.present();
    this.subTaskIds = params.parentTask.input
      .filter(inputElem => params.subTaskCondition(inputElem))
      .map(
        // TODO: Add logic that ensures that we are getting Tasks and have a valueReference
        inputElem => inputElem.valueReference.reference.split("/")[1]
      );
    const firstSubTaskId = this.subTaskIds[0];
    setTimeout(() => {
      this.store
        .select(getFlattenedFhir)
        .pipe(
          first(flattenedFhir => {
            return (
              flattenedFhir.hasOwnProperty(firstSubTaskId) &&
              flattenedFhir[firstSubTaskId].questionnaire !== null
            );
          })
        )
        .subscribe(flattenedFhir => {
          const questionnairePageParams = this.createQuestionnairePageParams({
            subTask: flattenedFhir[firstSubTaskId],
            sourceRef: params.sourceRef,
            subjectRef: params.subjectRef,
            authorRef: params.authorRef,
            optParams: {
              onSubmit: this.onSubmitOfQuestionnaire,
              ...params.overriddenParams
            }
          });
          const rootNav = this.app.getRootNav();
          rootNav.setRoot(QuestionnairePage, questionnairePageParams);
        });
    }, 1000);
  }

  submitItemEvalQuestionnaireResponse(
    values,
    subjectResponse: fhir.QuestionnaireResponse,
    patient: DiaprofilPatient,
    extraAction: () => void
  ) {
    this.fhirService
      .addTasks({
        owner_reference: this.userState.userFhir.id,
        requester_reference: this.userState.userFhir.id,
        patient_reference: patient.fhirId,
        task_type: "HCP_ITEM_NOTES",
        task_end_date: "2030-01-01", // TODO: Undo this hardcoded end date
        focus_reference: `QuestionnaireResponse/${subjectResponse.id}`,
        return_only_parent_task: false
      })
      .subscribe(resources => {
        const parentTask = resources.find(resource => {
          if (resource.resourceType === "Task") {
            const task = resource as fhir.Task;
            const isParentTask = codingMatch(task.code.coding, [
              {
                code: "parentTask",
                present: true
              }
            ]);
            if (isParentTask) {
              return true;
            }
          }
          return false;
        });
        const subTask = resources.find(resource => {
          if (resource.resourceType === "Task") {
            const task = resource as fhir.Task;
            const isSubTask = codingMatch(task.code.coding, [
              {
                code: "subTask",
                present: true
              }
            ]);
            if (isSubTask) {
              return true;
            }
          }
          return false;
        });
        const procedureRequest = resources.find(
          resource => resource.resourceType === "ProcedureRequest"
        );
        const userReference: fhir.Reference = {
          display: this.userState.userName,
          reference: `${this.userState.userFhir.resourceType}/${this.userState.userFhir.id}`
        };
        const responseReference: fhir.Reference = {
          display: subjectResponse.id,
          reference: `${subjectResponse.resourceType}/${subjectResponse.id}`
        };
        const questionnaire = this.fhirService.resolveReference(
          `Questionnaire/${DIAPROFIL_ITEM_EVAL_QUESTIONNAIRE_UUID}`
        ) as fhir.Questionnaire;
        const date = new Date();
        const questionnaireResponse = this.questionnaireService.createQuestionnaireResponse(
          {
            questionnaire: questionnaire,
            questionnaireResponse: null,
            taskId: subTask.id,
            procedureRequestId: procedureRequest.id,
            patientId: null,
            values: values,
            progress: 100,
            initDate: date,
            firstAnswerDate: date,
            latestAnswerDate: date,
            subjectRef: responseReference,
            authorRef: userReference,
            sourceRef: userReference
          },
          null
        );
        this.fhirService
          .postQuestionnaireResponse({
            responseResource: questionnaireResponse,
            procedureRequestId: procedureRequest.id,
            subTaskId: subTask.id
          })
          .subscribe(result =>
            this.fhirService
              .patchResource({
                resourceType: "Task",
                resourceId: parentTask.id,
                operations: [
                  {
                    op: "replace",
                    path: "/status",
                    value: "completed"
                  }
                ]
              })
              .subscribe(result => {
                this.events.publish("clear:itemEvals", {
                  responseId: subjectResponse.id
                });
                extraAction();
              })
          );
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/diaprofil.service.ts